/**
 * errorReporter — client-side JS error capture. Hooks `window.onerror` and
 * `unhandledrejection` and forwards a compact `client.error` event to
 * `/api/ops/client-event` via the shared queue in siteAnalytics.ts.
 *
 * Client-side rate limit: at most MAX_ERRORS_PER_WINDOW per window, so a render
 * loop or noisy third-party can't flood the endpoint (also IP-rate-limited
 * server-side at 120/min).
 */

import { enqueueClientEvent, getVisitorId } from './siteAnalytics';

const ERROR_WINDOW_MS = 10_000;
const MAX_ERRORS_PER_WINDOW = 5;
const MAX_MESSAGE = 200;
const MAX_FRAME = 200;

let installed = false;
let windowStartedAt = 0;
let errorsThisWindow = 0;

function report(kind: string, message: string, frame: string): void {
  if (!message) return;
  const now = Date.now();
  if (now - windowStartedAt > ERROR_WINDOW_MS) {
    windowStartedAt = now;
    errorsThisWindow = 0;
  }
  if (errorsThisWindow >= MAX_ERRORS_PER_WINDOW) return;
  errorsThisWindow += 1;

  enqueueClientEvent({
    event: 'client.error',
    route: typeof location !== 'undefined' ? location.pathname : '-',
    message: message.slice(0, MAX_MESSAGE),
    frame: frame.slice(0, MAX_FRAME),
    kind,
    visitorId: getVisitorId(),
    ts: now,
  });
}

/** One-time setup. Idempotent. Call after initSiteAnalytics(). */
export function initErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    const message = e.message || String(e.error ?? 'error');
    const frame = e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : '';
    report('error', message, frame);
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason: unknown = e.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const frame =
      reason instanceof Error && reason.stack
        ? (reason.stack.split('\n')[1] ?? '').trim()
        : '';
    report('unhandledrejection', message, frame);
  });
}
